import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './home.css';
import heroImg from '../assets/heroimg.png';
import arrowIcon from '../assets/arrow.png';

gsap.registerPlugin(ScrollTrigger);

const stats = [
  { value: 342, suffix: '', label: 'Exhibitors', detail: 'Leading manufacturers and suppliers' },
  { value: 45, suffix: '', label: 'Countries', detail: 'Global representation' },
  { value: 8500, suffix: '+', label: 'Visitors', detail: 'Qualified industry professionals' },
  { value: 4, suffix: '', label: 'Days', detail: 'Networking, demos, and sourcing' },
];

const advantages = [
  {
    title: 'Live Machinery Demonstrations',
    text: 'See textile automation, cutting systems, finishing equipment, and garment production technology working in real time.',
  },
  {
    title: 'High-Value Buyer Meetings',
    text: 'Connect with factory owners, sourcing teams, engineers, distributors, and decision makers from Egypt and the wider region.',
  },
  {
    title: 'Regional Market Access',
    text: 'Position your brand inside one of the Middle East and Africa market hubs for textile manufacturing growth.',
  },
];

const exhibitors = [
  { initials: 'CTI', name: 'Cairo Textile Ind.', type: 'Manufacturing' },
  { initials: 'DMC', name: 'Delta Machinery', type: 'Equipment' },
  { initials: 'AG', name: 'Alexandria Garm.', type: 'Textiles' },
  { initials: 'MTS', name: 'Modern Tech Solut.', type: 'Automation' },
  { initials: 'NIS', name: 'Nile Industrial Sys.', type: 'Manufacturing' },
];

const timeline = [
  'Registration & sourcing lounge',
  'Automation and smart factory demos',
  'Regional buyer matchmaking',
  'Sustainable production showcase',
];

const Home = () => {
  const pageRef = useRef(null);
  const heroRef = useRef(null);
  const [statCounts, setStatCounts] = useState(stats.map(() => 0));

  useEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;

    // Simple GSAP setup: hero entrance, scroll reveals, and parallax.
    const ctx = gsap.context(() => {
      gsap.from('.hero-line', {
        scaleX: 0,
        transformOrigin: 'left center',
        duration: 1.2,
        ease: 'power4.out',
        stagger: 0.12,
      });

      gsap.from('.gsap-hero', {
        y: 55,
        opacity: 0,
        filter: 'blur(10px)',
        duration: 1,
        ease: 'power4.out',
        stagger: 0.12,
      });

      gsap.to('.hero-disc-rings', {
        rotate: 360,
        duration: 34,
        repeat: -1,
        ease: 'none',
      });

      gsap.to('.orbit-dot', {
        rotate: -360,
        duration: 16,
        repeat: -1,
        ease: 'none',
      });

      gsap.utils.toArray('[data-reveal]').forEach((section) => {
        gsap.fromTo(
          section,
          { y: 60, filter: 'blur(8px)' },
          {
            y: 0,
            filter: 'blur(0px)',
            duration: 0.9,
            ease: 'power3.out',
            immediateRender: false,
            scrollTrigger: {
              trigger: section,
              start: 'top 88%',
              once: true,
            },
          }
        );
      });

      gsap.utils.toArray('[data-speed]').forEach((item) => {
        const speed = Number(item.dataset.speed);
        gsap.to(item, {
          y: speed * -90,
          ease: 'none',
          scrollTrigger: {
            trigger: item,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true,
          },
        });
      });

      ScrollTrigger.create({
        trigger: '.stats-band',
        start: 'top 82%',
        once: true,
        onEnter: () => {
          stats.forEach((stat, index) => {
            const counter = { value: 0 };

            gsap.to(counter, {
              value: stat.value,
              duration: 1.8,
              delay: index * 0.12,
              ease: 'power3.out',
              onUpdate: () => {
                setStatCounts((currentCounts) => {
                  const nextCounts = [...currentCounts];
                  nextCounts[index] = Math.round(counter.value);
                  return nextCounts;
                });
              },
            });
          });
        },
      });
    }, page);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return undefined;

    const handleMouseMove = (event) => {
      const rect = hero.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const moveX = (x / rect.width - 0.5) * 34;
      const moveY = (y / rect.height - 0.5) * 24;

      hero.style.setProperty('--mouse-x', `${x}px`);
      hero.style.setProperty('--mouse-y', `${y}px`);
      gsap.to('.hero-img', { x: moveX, y: moveY, duration: 0.8, ease: 'power3.out' });
      gsap.to('.hero-disc', { x: moveX * -0.45, y: moveY * -0.45, duration: 0.8, ease: 'power3.out' });
      gsap.to('.hero-vertical', { x: moveX * 0.25, duration: 0.8, ease: 'power3.out' });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const cards = document.querySelectorAll('.spotlight-card');
    const magneticItems = document.querySelectorAll('.magnetic');

    const moveSpotlight = (event) => {
      const card = event.currentTarget;
      const rect = card.getBoundingClientRect();
      card.style.setProperty('--spot-x', `${event.clientX - rect.left}px`);
      card.style.setProperty('--spot-y', `${event.clientY - rect.top}px`);
    };

    const moveMagnet = (event) => {
      const item = event.currentTarget;
      const rect = item.getBoundingClientRect();
      const x = event.clientX - rect.left - rect.width / 2;
      const y = event.clientY - rect.top - rect.height / 2;
      gsap.to(item, { x: x * 0.22, y: y * 0.22, duration: 0.35, ease: 'power3.out' });
    };

    const resetMagnet = (event) => {
      gsap.to(event.currentTarget, { x: 0, y: 0, duration: 0.55, ease: 'elastic.out(1, 0.35)' });
    };

    cards.forEach((card) => card.addEventListener('mousemove', moveSpotlight));
    magneticItems.forEach((item) => {
      item.addEventListener('mousemove', moveMagnet);
      item.addEventListener('mouseleave', resetMagnet);
    });

    return () => {
      cards.forEach((card) => card.removeEventListener('mousemove', moveSpotlight));
      magneticItems.forEach((item) => {
        item.removeEventListener('mousemove', moveMagnet);
        item.removeEventListener('mouseleave', resetMagnet);
      });
    };
  }, []);

  return (
    <main className="home" ref={pageRef}>
      <section className="hero" ref={heroRef}>
        <div className="hero-img-wrap">
          <img src={heroImg} alt="Stacked textile fabrics" className="hero-img" />
        </div>
        <div className="hero-overlay" />
        <div className="hero-noise" />
        <span className="hero-line hero-line-one" />
        <span className="hero-line hero-line-two" />
        <span className="hero-line hero-line-three" />
        <strong className="hero-vertical gsap-hero">TEXTILE</strong>

        <div className="hero-content">
          <div className="hero-kicker gsap-hero">April 20-23, 2026 / Cairo, Egypt</div>
          <h1 className="hero-title gsap-hero">
            Egypt International Exhibition for Garment &amp; Textile Machinery
          </h1>
          <p className="hero-sub gsap-hero">
            A premium industrial meeting point for manufacturers, buyers, distributors,
            and technology leaders shaping the future of textile production.
          </p>

          <div className="hero-actions gsap-hero">
            <Link to="/exhibitors" className="btn btn-primary magnetic">
              Explore Exhibitors
              <img src={arrowIcon} alt="" className="arrow-icon" aria-hidden="true" />
            </Link>
            <Link to="/tickets" className="btn btn-outline magnetic">
              Book Ticket
            </Link>
          </div>
        </div>

        <div className="hero-disc gsap-hero" data-speed="0.45">
          <div className="hero-disc-rings" aria-hidden="true">
            <span className="orbit-dot" />
            <span className="orbit-dot orbit-dot-two" />
          </div>
          <div className="hero-disc-text">
            <small>2026 Edition</small>
            <strong>Industrial scale</strong>
          </div>
        </div>

        <aside className="hero-panel gsap-hero spotlight-card" aria-label="Event highlights">
          <span className="panel-label">2026 Edition</span>
          <strong>Where garment innovation meets industrial scale.</strong>
          <div className="panel-meter">
            <span />
          </div>
          <p>Hall A, B, C / Cairo International Convention Centre</p>
        </aside>
      </section>

      <div className="ticker" aria-label="Event facts">
        <div className="ticker-track">
          {[...Array(2)].map((_, index) => (
            <React.Fragment key={index}>
              <span>342 Exhibitors</span>
              <span>45 Countries</span>
              <span>8,500+ Visitors</span>
              <span>4 Event Days</span>
              <span>Cairo 2026</span>
              <span>20+ Years of Excellence</span>
            </React.Fragment>
          ))}
        </div>
      </div>

      <section className="stats-band" data-reveal>
        {stats.map((stat, index) => (
          <article className="stat-card spotlight-card" key={stat.label} style={{ '--delay': `${index * 90}ms` }}>
            <span>{stat.label}</span>
            <strong>
              {statCounts[index].toLocaleString()}
              {stat.suffix}
            </strong>
            <p>{stat.detail}</p>
          </article>
        ))}
      </section>

      <section className="intro-section">
        <div className="section-copy" data-reveal>
          <span className="eyebrow">Industry Platform</span>
          <h2>The region's premier stage for textile industry innovation.</h2>
          <p>
            Built for serious sourcing and practical discovery, the exhibition brings
            together machinery makers, garment factories, textile suppliers, and
            production specialists under one high-energy Cairo event.
          </p>
        </div>

        <div className="feature-stack" data-reveal data-speed="0.18">
          {advantages.map((item, index) => (
            <article className="feature-card spotlight-card" key={item.title} style={{ '--delay': `${index * 120}ms` }}>
              <span>0{index + 1}</span>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="exhibitors-section" data-reveal>
        <div className="section-heading">
          <span className="eyebrow">Featured Exhibitors</span>
          <h2>Meet Industry Leaders</h2>
          <p>
            Explore premier manufacturers and suppliers showcasing next-generation
            machinery, automation, and garment production technology.
          </p>
        </div>

        <div className="exhibitor-grid">
          {exhibitors.map((exhibitor, index) => (
            <article className="exhibitor-card spotlight-card" key={exhibitor.name} style={{ '--delay': `${index * 85}ms` }}>
              <div className="exhibitor-mark">{exhibitor.initials}</div>
              <div>
                <h3>{exhibitor.name}</h3>
                <span>{exhibitor.type}</span>
              </div>
              <Link to="/exhibitors" className="magnetic" aria-label={`View ${exhibitor.name}`}>
                <img src={arrowIcon} alt="" className="arrow-icon" aria-hidden="true" />
              </Link>
            </article>
          ))}
        </div>

        <Link to="/exhibitors" className="btn btn-dark magnetic">
          View All Exhibitors
          <img src={arrowIcon} alt="" className="arrow-icon" aria-hidden="true" />
        </Link>
      </section>

      <section className="program-section">
        <div className="program-card spotlight-card" data-reveal>
          <span className="eyebrow">Event Flow</span>
          <h2>Four focused days of sourcing, demos, and industry meetings.</h2>
          <div className="timeline">
            {timeline.map((item, index) => (
              <div className="timeline-item" key={item}>
                <span>{index + 1}</span>
                <p>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-section spotlight-card" data-reveal>
        <div>
          <span className="eyebrow">Exhibit With Impact</span>
          <h2>Showcase your innovation to 8,500+ industry professionals.</h2>
          <p>
            Secure premium visibility, build qualified leads, and place your brand
            inside Egypt's most focused textile machinery exhibition.
          </p>
        </div>
        <div className="cta-actions">
          <Link to="/contact" className="btn btn-primary magnetic">
            Become an Exhibitor
            <img src={arrowIcon} alt="" className="arrow-icon" aria-hidden="true" />
          </Link>
          <Link to="/tickets" className="btn btn-outline light magnetic">
            Download Brochure
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Home;
